import { mergeWith, pick } from 'lodash';

import { replacePresentationSubObjectsMergeCustomizer } from '~/swr-hooks/shortUrl/replacePresentationSubObjectsMergeCustomizer';
import { type AssetScopedShareLinkListItem } from '~/swr-hooks/shortUrl/useListAssetShareLinks';
import { type ShareUrlUpdateHandlerProps } from '~/swr-hooks/shortUrl/useUpdateShortId';

/**
 * Fields allowed from the list-row base before merging the patch.
 * Conversion `objectType` (clip|asset) re-enters via the patch.
 */
export const UPDATABLE_ASSET_SHARE_LINK_FIELDS = [
  'passcode',
  'disabled',
  'requireAccount',
  'expirationDate',
  'permissions',
  'presentationSettings',
  'presentationContent',
  'presentationPages',
  'boardContext',
] as const;

type AssetShareLinkUpdateBaseline = Pick<
  AssetScopedShareLinkListItem,
  (typeof UPDATABLE_ASSET_SHARE_LINK_FIELDS)[number]
>;

const getAssetShareLinkApiBaseline = (shortIdInfo: AssetScopedShareLinkListItem): AssetShareLinkUpdateBaseline => {
  const baseline = pick(shortIdInfo, UPDATABLE_ASSET_SHARE_LINK_FIELDS);
  const presentationSettings = baseline.presentationSettings;

  if (!presentationSettings) {
    return baseline;
  }

  // Workspace defaults persist `customFonts` onto new asset/clip links; the update API rejects
  // that field for those object types, so it must not ride along on an unrelated PATCH.
  const { customFonts: _customFonts, ...presentationSettingsWithoutCustomFonts } = presentationSettings;

  return {
    ...baseline,
    presentationSettings: presentationSettingsWithoutCustomFonts,
  };
};

export const mergeAssetShareLinkUpdatePatch = (
  shortIdInfo: AssetScopedShareLinkListItem,
  patch: ShareUrlUpdateHandlerProps,
) =>
  mergeWith(
    {},
    pick(shortIdInfo, UPDATABLE_ASSET_SHARE_LINK_FIELDS),
    patch,
    replacePresentationSubObjectsMergeCustomizer,
  );

export const mergeAssetShareLinkApiUpdatePatch = (
  shortIdInfo: AssetScopedShareLinkListItem,
  patch: ShareUrlUpdateHandlerProps,
): ReturnType<typeof mergeAssetShareLinkUpdatePatch> => {
  const merged = mergeWith(
    {},
    getAssetShareLinkApiBaseline(shortIdInfo),
    patch,
    replacePresentationSubObjectsMergeCustomizer,
  );

  if (patch.presentationSettings !== undefined) {
    return merged;
  }

  // `presentationSettings` is a shallow PATCH — omit the stored object entirely when this save
  // did not touch it, so leftover branding keys cannot be re-validated.
  const { presentationSettings: _presentationSettings, ...withoutPresentationSettings } = merged;

  return withoutPresentationSettings;
};
